import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import {
  getPatientServices,
  createPatientService,
  getBarangays,
  getMedicines,
} from '../../lib/api';
import {
  Stethoscope,
  Plus,
  Search,
  User,
  Calendar,
  Filter,
} from 'lucide-react';

const COMPLAINT_OPTIONS = [
  'Fever', 'Cough', 'Cold', 'Headache', 'Diarrhea', 'Vomiting',
  'Skin Rash', 'Wound', 'Dengue', 'Hypertension', 'Diabetes',
  'Asthma', 'UTI', 'Back Pain', 'Joint Pain', 'Other'
];

const SERVICE_OPTIONS = [
  'Consultation', 'Medication', 'Wound Dressing', 'Blood Pressure Check',
  'Blood Sugar Test', 'Prenatal Checkup', 'Immunization', 'Health Certificate',
  'Medical Certificate', 'Other'
];

export default function HealthLogbook() {
  const { user, isMunicipalAdmin } = useAuth();
  const [services, setServices] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBarangay, setFilterBarangay] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    barangay_id: '',
    patient_name: '',
    age: '',
    sex: 'Male',
    is_resident: true,
    chief_complaint: '',
    service_provided: '',
    medicine_dispensed: '',
    medicine_quantity: '',
    referred: false,
    referral_destination: '',
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [servicesRes, barangaysRes, medicinesRes] = await Promise.all([
        getPatientServices(),
        getBarangays(),
        getMedicines(),
      ]);
      setServices(servicesRes.data);
      setBarangays(barangaysRes.data);
      setMedicines(medicinesRes.data);

      if (!isMunicipalAdmin() && user?.barangay_id) {
        setFormData(prev => ({ ...prev, barangay_id: user.barangay_id }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load health records');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const data = {
        ...formData,
        age: parseInt(formData.age),
        medicine_quantity: formData.medicine_quantity ? parseInt(formData.medicine_quantity) : null,
      };
      
      await createPatientService(data);
      toast.success('Patient service recorded successfully');
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record service');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      barangay_id: !isMunicipalAdmin() ? user?.barangay_id || '' : '',
      patient_name: '',
      age: '',
      sex: 'Male',
      is_resident: true,
      chief_complaint: '',
      service_provided: '',
      medicine_dispensed: '',
      medicine_quantity: '',
      referred: false,
      referral_destination: '',
    });
  };

  const filteredServices = services.filter(service => {
    const matchesSearch = service.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.chief_complaint.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBarangay = filterBarangay === 'all' || service.barangay_id === filterBarangay;
    return matchesSearch && matchesBarangay;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="health-logbook">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Health Logbook
            </h1>
            <p className="text-muted-foreground mt-1">
              Patient service records and consultations
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-patient-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Entry
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Patient Service Entry</DialogTitle>
                <DialogDescription>
                  Record a new patient consultation or service
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {isMunicipalAdmin() && (
                    <div className="space-y-2">
                      <Label>Barangay *</Label>
                      <Select
                        value={formData.barangay_id}
                        onValueChange={(v) => setFormData({ ...formData, barangay_id: v })}
                      >
                        <SelectTrigger data-testid="barangay-select">
                          <SelectValue placeholder="Select barangay" />
                        </SelectTrigger>
                        <SelectContent>
                          {barangays.map((b) => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Patient Name *</Label>
                    <Input
                      value={formData.patient_name}
                      onChange={(e) => setFormData({ ...formData, patient_name: e.target.value })}
                      placeholder="Enter patient name"
                      required
                      data-testid="patient-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Age *</Label>
                    <Input
                      type="number"
                      value={formData.age}
                      onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                      placeholder="Age"
                      required
                      min="0"
                      max="150"
                      data-testid="patient-age-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Sex *</Label>
                    <Select
                      value={formData.sex}
                      onValueChange={(v) => setFormData({ ...formData, sex: v })}
                    >
                      <SelectTrigger data-testid="sex-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Barangay Resident?</Label>
                    <Select
                      value={formData.is_resident ? 'yes' : 'no'}
                      onValueChange={(v) => setFormData({ ...formData, is_resident: v === 'yes' })}
                    >
                      <SelectTrigger data-testid="resident-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Chief Complaint *</Label>
                    <Select
                      value={formData.chief_complaint}
                      onValueChange={(v) => setFormData({ ...formData, chief_complaint: v })}
                    >
                      <SelectTrigger data-testid="complaint-select">
                        <SelectValue placeholder="Select complaint" />
                      </SelectTrigger>
                      <SelectContent>
                        {COMPLAINT_OPTIONS.map((c) => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Service Provided *</Label>
                    <Select
                      value={formData.service_provided}
                      onValueChange={(v) => setFormData({ ...formData, service_provided: v })}
                    >
                      <SelectTrigger data-testid="service-select">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent>
                        {SERVICE_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Medicine Dispensed</Label>
                    <Select
                      value={formData.medicine_dispensed || '__none__'}
                      onValueChange={(v) => setFormData({ ...formData, medicine_dispensed: v === '__none__' ? '' : v })}
                    >
                      <SelectTrigger data-testid="medicine-select">
                        <SelectValue placeholder="Select medicine (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">None</SelectItem>
                        {medicines.map((m) => (
                          <SelectItem key={m.id} value={m.name}>
                            {m.name} (Stock: {m.quantity})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.medicine_dispensed && (
                    <div className="space-y-2">
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        value={formData.medicine_quantity}
                        onChange={(e) => setFormData({ ...formData, medicine_quantity: e.target.value })}
                        placeholder="Quantity"
                        min="1"
                        data-testid="medicine-quantity-input"
                      />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label>Referred?</Label>
                    <Select
                      value={formData.referred ? 'yes' : 'no'}
                      onValueChange={(v) => setFormData({ ...formData, referred: v === 'yes' })}
                    >
                      <SelectTrigger data-testid="referred-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="yes">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.referred && (
                    <div className="space-y-2">
                      <Label>Referral Destination</Label>
                      <Input
                        value={formData.referral_destination}
                        onChange={(e) => setFormData({ ...formData, referral_destination: e.target.value })}
                        placeholder="Hospital or clinic name"
                        data-testid="referral-input"
                      />
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="submit-patient-btn">
                    {formLoading ? 'Saving...' : 'Save Entry'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by patient name or complaint..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              {isMunicipalAdmin() && (
                <Select value={filterBarangay} onValueChange={setFilterBarangay}>
                  <SelectTrigger className="w-full md:w-[200px]" data-testid="filter-barangay">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Filter by barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Barangays</SelectItem>
                    {barangays.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-primary" />
              Patient Records
            </CardTitle>
            <CardDescription>
              {filteredServices.length} records found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Patient</TableHead>
                    <TableHead>Age/Sex</TableHead>
                    <TableHead>Complaint</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Medicine</TableHead>
                    {isMunicipalAdmin() && <TableHead>Barangay</TableHead>}
                    <TableHead>Referred</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredServices.map((service) => (
                    <TableRow key={service.id} data-testid={`service-row-${service.id}`}>
                      <TableCell className="text-sm">
                        {new Date(service.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="font-medium">{service.patient_name}</TableCell>
                      <TableCell>
                       {service.age ?? '-'} / {service.sex ? service.sex.charAt(0) : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{service.chief_complaint}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{service.service_provided}</TableCell>
                      <TableCell className="text-sm">
                        {service.medicine_dispensed ? (
                          <span>{service.medicine_dispensed} ({service.medicine_quantity})</span>
                        ) : '-'}
                      </TableCell>
                      {isMunicipalAdmin() && (
                        <TableCell className="text-sm">{getBarangayName(service.barangay_id)}</TableCell>
                      )}
                      <TableCell>
                        {service.referred ? (
                          <Badge variant="outline" className="text-amber-600 border-amber-300">
                            {service.referral_destination || 'Yes'}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {filteredServices.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMunicipalAdmin() ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
